import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import { z } from 'zod/v3';
import { AnimatedCubeIcon } from '@/components/animated/animated-cube-icon';
import { useAuth } from '@/context/auth-context';
import { isApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

const loginSchema = z.object({
  username: z.string().min(1, 'Usuário ou e-mail é obrigatório'),
  password: z.string().min(1, 'Senha é obrigatória'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/home';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError(null);
      await login(data);
      navigate(from, { replace: true });
    } catch (err) {
      if (isApiError(err)) {
        setError(err.response?.data.message || 'Erro ao fazer login');
      } else {
        setError('Erro ao fazer login. Tente novamente.');
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8 bg-gray-50">
      {/* Card - matching existing Next.js Card component */}
      <div className="w-full max-w-6xl h-[40rem] lg:h-[40rem] max-h-[90vh] p-20 rounded-xl border border-gray-200 bg-white text-gray-900 shadow">
        <div className="flex flex-col lg:flex-row h-full">
          {/* Left side - Logo with grid background */}
          <div className="hidden lg:flex flex-1 items-center justify-center relative">
            <div
              className={cn(
                'absolute inset-0 rounded-l-xl',
                '[background-size:40px_40px]',
                '[background-image:linear-gradient(to_right,#d4d4d8_1px,transparent_1px),linear-gradient(to_bottom,#d4d4d8_1px,transparent_1px)]',
              )}
            />

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] rounded-l-xl" />

            <div className="relative text-center">
              <AnimatedCubeIcon size={60} className="mb-8 mx-auto" />
              <p className="bg-gradient-to-b from-gray-300 to-black bg-clip-text text-4xl font-bold text-transparent lg:text-5xl">
                BCUBE
              </p>
            </div>
          </div>

          {/* Right side - Login form */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-md">
              {/* Card Header */}
              <div className="text-center px-0">
                {/* Show smaller animated cube on mobile - hidden on large screens */}
                <div className="lg:hidden mb-6">
                  <AnimatedCubeIcon size={40} className="mx-auto" />
                </div>

                <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                  Faça login na sua conta
                </h1>
              </div>

              {/* Card Content */}
              <div className="px-0 mt-4">
                {error && (
                  <div
                    role="alert"
                    className="mb-6 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-600"
                  >
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label
                        htmlFor="username"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Usuário ou e-mail
                      </label>
                      <input
                        id="username"
                        type="text"
                        autoComplete="username"
                        {...register('username')}
                        className={cn(
                          'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors',
                          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                          'placeholder:text-gray-400',
                          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                          'disabled:cursor-not-allowed disabled:opacity-50',
                          errors.username ? 'border-red-500' : 'border-gray-300',
                        )}
                      />
                      {errors.username && (
                        <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Senha
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          {...register('password')}
                          className={cn(
                            'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 pr-10 text-sm shadow-sm transition-colors',
                            'placeholder:text-gray-400',
                            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            errors.password ? 'border-red-500' : 'border-gray-300',
                          )}
                        />
                        <button
                          type="button"
                          aria-label="Alternar visibilidade da senha"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
                      Esqueci minha senha
                    </Link>
                  </div>

                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={cn(
                        'inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium',
                        'bg-gray-900 text-white shadow',
                        'hover:bg-gray-800',
                        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                        'disabled:pointer-events-none disabled:opacity-50',
                      )}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Entrando...
                        </>
                      ) : (
                        'Login'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
